import { ensureArray } from '@helpers/content/ensure-helpers-core';
import { ensureDroppedReward } from '@helpers/content/ensure-helpers-drops';
import {
  ensureCombatStats,
  ensureStats,
  ensureTagResistances,
} from '@helpers/content/ensure-helpers-stats';
import type {
  CommissionOfferId,
  CommissionOfferSlot,
  GlobalEffectId,
  ItemId,
  MonsterId,
  TownContent,
  TownContentInput,
  TownCraftingConfig,
  TownDefenseAssaulterConfig,
  TownDefenseBuyoffConfig,
  TownDefenseConfig,
  TownDefenseFortifyMaterial,
  TownDefenseGuardianConfig,
  TownDefenseGuardianEntry,
  TownDefenseGuardianReputationTier,
  TownDefenseQuestsConfig,
  TownGatheringConfig,
  TownGatheringWorker,
  TownId,
  TownMaterialThreshold,
  TownReputationBuffTier,
  TownReputationConfig,
  TownReputationTierValue,
  TownTradersConfig,
  TownTradeskillLevelSeed,
  TradeskillId,
  WorkerId,
} from '@interfaces';

function ensureTownTradeskillLevelSeed(
  seed: Partial<TownTradeskillLevelSeed> = {},
): TownTradeskillLevelSeed {
  return {
    tradeskillId: seed.tradeskillId ?? ('UNKNOWN' as TradeskillId),
    level: seed.level ?? 1,
  };
}

export function ensureTownReputationTierValue(
  tier: Partial<TownReputationTierValue> = {},
): TownReputationTierValue {
  return {
    tier: tier.tier ?? 0,
    value: tier.value ?? 1,
  };
}

function ensureTownCrafting(
  crafting: TownContentInput['crafting'] = {},
): TownCraftingConfig {
  return {
    maxQueueSize: ensureArray(
      crafting.maxQueueSize,
      ensureTownReputationTierValue,
    ),
    specialtyTradeskillId:
      crafting.specialtyTradeskillId ?? ('UNKNOWN' as TradeskillId),
    craftingDurationMultiplier: crafting.craftingDurationMultiplier ?? 1,
    craftingChanceOnTick: crafting.craftingChanceOnTick ?? 100,
    craftingChanceItemThreshold: crafting.craftingChanceItemThreshold ?? 1,
    tradeskillLevels: ensureArray(
      crafting.tradeskillLevels,
      ensureTownTradeskillLevelSeed,
    ),
    uniqueRecipeIds: crafting.uniqueRecipeIds ?? [],
    bannedRecipeIds: crafting.bannedRecipeIds ?? [],
  };
}

function ensureTownTraders(
  traders: TownContentInput['traders'] = {},
): TownTradersConfig {
  return {
    sellItemCount: ensureArray(
      traders.sellItemCount,
      ensureTownReputationTierValue,
    ),
    itemExpirationTimer: traders.itemExpirationTimer ?? 0,
    markupPercentages: traders.markupPercentages ?? { sell: 0, buy: 0 },
  };
}

function ensureTownGatheringWorker(
  worker: Partial<TownGatheringWorker> = {},
): TownGatheringWorker {
  return {
    workerId: worker.workerId ?? ('UNKNOWN' as WorkerId),
    level: worker.level ?? 1,
  };
}

function ensureTownMaterialThreshold(
  threshold: Partial<TownMaterialThreshold> = {},
): TownMaterialThreshold {
  return {
    itemId: threshold.itemId ?? ('UNKNOWN' as ItemId),
    maxQuantity: threshold.maxQuantity ?? 0,
    value: threshold.value ?? 0,
    sellAtQuantity: threshold.sellAtQuantity ?? 0,
    default: threshold.default ?? 0,
  };
}

function ensureTownGathering(
  gathering: TownContentInput['gathering'] = {},
): TownGatheringConfig {
  return {
    gatherRateMultiplier: gathering.gatherRateMultiplier ?? 1,
    goldGatheredPerMaterial: gathering.goldGatheredPerMaterial ?? 0,
    workers: ensureArray(gathering.workers, ensureTownGatheringWorker),
  };
}

function ensureTownReputationBuffTier(
  tier: NonNullable<
    NonNullable<NonNullable<TownContentInput['reputation']>['buff']>['tiers']
  >[number] = {},
): TownReputationBuffTier {
  return {
    tier: tier.tier ?? 0,
    stats: ensureStats(tier.stats),
    combatStats: ensureCombatStats(tier.combatStats),
    debuffResistances: ensureTagResistances(tier.debuffResistances),
  };
}

function ensureTownReputation(
  reputation: TownContentInput['reputation'] = {},
): TownReputationConfig {
  return {
    buff: {
      globalEffectId:
        reputation.buff?.globalEffectId ?? ('UNKNOWN' as GlobalEffectId),
      tiers: ensureArray(reputation.buff?.tiers, ensureTownReputationBuffTier),
    },
  };
}

function ensureTownDefenseGuardianEntry(
  entry: Partial<TownDefenseGuardianEntry> = {},
): TownDefenseGuardianEntry {
  return {
    monsterId: entry.monsterId ?? ('UNKNOWN' as MonsterId),
    quantity: entry.quantity ?? 0,
  };
}

function ensureTownDefenseGuardianReputationTier(
  tier: NonNullable<
    NonNullable<
      NonNullable<TownContentInput['defense']>['guardian']
    >['reputationTiers']
  >[number] = {},
): TownDefenseGuardianReputationTier {
  return {
    tier: tier.tier ?? 0,
    guardians: ensureArray(tier.guardians, ensureTownDefenseGuardianEntry),
  };
}

function ensureTownDefenseGuardian(
  guardian: NonNullable<TownContentInput['defense']>['guardian'] = {},
): TownDefenseGuardianConfig {
  return {
    reputationTiers: ensureArray(
      guardian.reputationTiers,
      ensureTownDefenseGuardianReputationTier,
    ),
  };
}

function ensureTownDefenseAssaulter(
  assaulter: Partial<TownDefenseAssaulterConfig> = {},
): TownDefenseAssaulterConfig {
  return {
    numMonsters: assaulter.numMonsters ?? 0,
    monsterIds: assaulter.monsterIds ?? [],
    level: assaulter.level ?? { min: 1, max: 1 },
  };
}

function ensureTownCommissionOfferSlot(
  slot: Partial<CommissionOfferSlot> = {},
): CommissionOfferSlot {
  return {
    commissionOfferId:
      slot.commissionOfferId ?? ('UNKNOWN' as CommissionOfferId),
    weight: slot.weight ?? 1,
    persistent: slot.persistent ?? false,
  };
}

function ensureTownDefenseQuests(
  quests: NonNullable<TownContentInput['defense']>['quests'] = {},
): TownDefenseQuestsConfig {
  return {
    commissions: ensureArray(quests.commissions, ensureTownCommissionOfferSlot),
  };
}

function ensureTownDefenseFortifyMaterial(
  material: Partial<TownDefenseFortifyMaterial> = {},
): TownDefenseFortifyMaterial {
  return {
    itemId: material.itemId ?? ('UNKNOWN' as ItemId),
    quantityPerAssaulter: material.quantityPerAssaulter ?? 1,
  };
}

function ensureTownDefenseBuyoff(
  buyoff: NonNullable<TownContentInput['defense']>['buyoff'] = {},
): TownDefenseBuyoffConfig {
  return {
    tributeGoldScalar: buyoff.tributeGoldScalar ?? 0,
    fortifyMaterials: ensureArray(
      buyoff.fortifyMaterials,
      ensureTownDefenseFortifyMaterial,
    ),
  };
}

function ensureTownDefense(
  defense: TownContentInput['defense'] = {},
): TownDefenseConfig {
  return {
    rewards: ensureArray(defense.rewards, ensureDroppedReward),
    guardian: ensureTownDefenseGuardian(defense.guardian),
    assaulter: ensureTownDefenseAssaulter(defense.assaulter),
    quests: ensureTownDefenseQuests(defense.quests),
    buyoff: ensureTownDefenseBuyoff(defense.buyoff),
  };
}

export function ensureTown(town: TownContentInput): Required<TownContent> {
  return {
    id: town.id ?? ('UNKNOWN' as TownId),
    name: town.name ?? 'UNKNOWN',
    __type: 'town',
    description: town.description ?? 'UNKNOWN',
    hidden: town.hidden ?? false,
    invisibleUntilCollectibleIdsFound:
      town.invisibleUntilCollectibleIdsFound ?? [],
    scaleType: town.scaleType ?? 'Outpost',
    level: town.level ?? 1,
    materialThresholds: ensureArray(
      town.materialThresholds,
      ensureTownMaterialThreshold,
    ),
    crafting: ensureTownCrafting(town.crafting),
    traders: ensureTownTraders(town.traders),
    gathering: ensureTownGathering(town.gathering),
    reputation: ensureTownReputation(town.reputation),
    defense: ensureTownDefense(town.defense),
  };
}
